// DEPENDENCIES
import React, { useState, useEffect, useContext } from "react";

// CSS
import "./DeviceStatusesConsole.css";

// MSAL
import { useMsal } from "@azure/msal-react";

// API IMPORTS
import { getAPICall, postAPICall } from "../../../config/apiCalls";

// CONTEXT
import { UserContext } from "../../../context/UserContext";

// MUI COMPONENTS
import { Button, CircularProgress, IconButton } from "@mui/material";

// MUI ICONS
import EditIcon from "@mui/icons-material/Edit";

// CUSTOM COMPONENTS
import J2NoResultsFound from "../../Misc/J2NoResultsFound/J2NoResultsFound";
import DeviceStatusesConsoleEditAddDialog from "./DeviceStatusesConsoleEditAddDialog/DeviceStatusesConsoleEditAddDialog";
import TanStackTable from "../../Misc/Table/TanStackTable";

// CONSTANTS
import {
	defaultSort,
	notApplicable,
} from "../../Misc/Table/TanStackTableConstants";

// OTHER

// REACT COMPONENT
const DeviceStatusesConsole = (props) => {
	// MSAL
	const { instance, accounts } = useMsal();

	// STATES
	const [deviceStatuses, setDeviceStatuses] = useState([]);
	const [deviceStatusesRows, setDeviceStatusesRows] = useState([]);
	const [deviceStatusesColumns, setDeviceStatusesColumns] = useState([]);

	const [recordToEdit, setRecordToEdit] = useState({});
	const [loading, setLoading] = useState(false);
	const [editAddDialogOpen, setEditAddDialogOpen] = useState(false);
	const [editing, setEditing] = useState(false);

	// CONTEXT
	const employeeID = useContext(UserContext);

	// USE EFFECT
	useEffect(() => {
		postAPICall(instance, accounts[0], "/api/logs/access/add", {
			page_name: "Device Statuses Console",
			employeeID: employeeID,
			notes: "Accessed the Device Statuses Console page",
		}).catch(() => {});
	}, []);

	useEffect(() => {
		handleAPICalls();
	}, []);

	useEffect(() => {
		createRowData();
		createColumnData();
	}, [deviceStatuses]);

	// INPUT HANDLERS
	const handleAddClick = () => {
		setEditing(false);
		setEditAddDialogOpen(true);
	};

	const handleEditClick = (record) => {
		setRecordToEdit(record);
		setEditing(true);
		setEditAddDialogOpen(true);
	};

	const handleEditAddDialogClose = () => {
		setEditing(false);
		setEditAddDialogOpen(false);
		setRecordToEdit({});
		handleAPICalls();
	};

	// API FUNCTIONS

	const handleAPICalls = async () => {
		setLoading(true);
		await getDeviceStatuses();
		setLoading(false);
	};

	const getDeviceStatuses = async () => {
		try {
			let res = await getAPICall(
				instance,
				accounts[0],
				`/api/hardwareInventory/deviceStatuses/all`
			);
			setDeviceStatuses(res.data);
		} catch (error) {
			console.error(error);
		}
	};

	// HELPER FUNCTIONS

	const createColumnData = () => {
		setDeviceStatusesColumns([
			{
				accessorKey: "name",
				header: () => <span>Name</span>,
				label: "Name",
				cell: ({ row }) => <p>{row.original.name || notApplicable}</p>,
				filterType: "textfield",
				filterFn: defaultSort,
			},
			{
				accessorKey: "display_name",
				header: () => <span>Display Name</span>,
				label: "Display Name",
				cell: ({ row }) => (
					<p>{row.original.display_name || notApplicable}</p>
				),
				filterType: "textfield",
				filterFn: defaultSort,
			},
			{
				accessorKey: "edit",
				header: "",
				label: "Edit",
				cell: ({ row }) => (
					<IconButton
						onClick={() => handleEditClick(row.original.record)}
						disableRipple
						sx={{ justifyContent: "flex-end" }}
					>
						<EditIcon fontSize="small" />
					</IconButton>
				),
				enableColumnFilter: false,
				enableSorting: false,
			},
		]);
	};

	const createRowData = () => {
		setDeviceStatusesRows(
			deviceStatuses.map((record) => ({
				record: record,
				name: record.name,
				display_name: record.display_name,
			}))
		);
	};

	// RENDER
	return (
		<div className="DeviceStatusesConsole">
			<div className="DeviceStatusesConsoleHeader">
				<div className="DeviceStatusesConsoleHeaderTitle">
					Device Status Console
					<Button
						variant="contained"
						color="primary"
						onClick={handleAddClick}
						disabled={loading}
					>
						New Device Status
					</Button>
				</div>
			</div>
			{loading && (
				<div className="loadingDiv">
					<CircularProgress />
				</div>
			)}
			{!loading && (
				<div className="tableContainerDiv">
					{deviceStatuses.length === 0 && <J2NoResultsFound />}
					{deviceStatuses.length > 0 &&
						deviceStatusesRows.length > 0 &&
						deviceStatusesColumns.length > 0 && (
							<TanStackTable
								columnData={deviceStatusesColumns}
								rowData={deviceStatusesRows}
							/>
						)}
				</div>
			)}
			<DeviceStatusesConsoleEditAddDialog
				open={editAddDialogOpen}
				onClose={handleEditAddDialogClose}
				recordToEdit={recordToEdit}
				editing={editing}
			/>
		</div>
	);
};

// EXPORT
export default DeviceStatusesConsole;
